import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase/firebaseConfig';
import ActualizarEmpresaDb from '../firebase/ActualizarEmpresaDb';
import Alerta from './Alertas';
import useObtenerEmpresa from '../hooks/useObtenerEmpresa';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Segment,
    Header,
    Form,
    Button,
    Icon,
    Grid,
    Image,
    Divider,
    Label,
    Card,
} from 'semantic-ui-react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { subirLogoEmpresa, getLogoUrl } from '../utils/logoEmpresa';

const ActualizaEmpresa = () => {
    const user = auth.currentUser;
    const navigate = useNavigate();
    const { id } = useParams();

    const [empresas] = useObtenerEmpresa(id);

    const [nuevaEmpresa, setNuevaEmpresa] = useState('');
    const [rut, setRut] = useState('');
    const [plan, setPlan] = useState('BASICO');
    const [estado, setEstado] = useState('ACTIVA');
    const [maxUsers, setMaxUsers] = useState(5);

    const [logoUrl, setLogoUrl] = useState('');
    const [logoPath, setLogoPath] = useState('');
    const [versionLogo, setVersionLogo] = useState(1);
    const [logoFile, setLogoFile] = useState(null);
    const [previewLogo, setPreviewLogo] = useState('');

    const [alerta, cambiarAlerta] = useState({});
    const [estadoAlerta, cambiarEstadoAlerta] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [contratoMantencion, setContratoMantencion] = useState(false);
    const [usaLogisticmedSt, setUsaLogisticmedSt] = useState(false);

    const logoSrc =
        previewLogo ||
        (logoUrl ? getLogoUrl(logoUrl, versionLogo) : '/logo-default.png');

    useEffect(() => {
        if (empresas) {
            setNuevaEmpresa(empresas.empresa || '');
            setRut(empresas.rut || '');
            setPlan(empresas.plan || 'BASICO');
            setEstado(empresas.estado || 'ACTIVA');
            setMaxUsers(empresas.maxUsers || 5);
            setLogoUrl(empresas.logoUrl || '');
            setLogoPath(empresas.logoPath || '');
            setVersionLogo(empresas.versionLogo || 1);
            setContratoMantencion(empresas.contrato_mantencion === true);
            setUsaLogisticmedSt(empresas.usa_logisticmed_st === true);
        } else if (empresas === null) {
            navigate('/configuracion/agregarempresa');
        }
    }, [empresas, navigate]);

    const volver = () => navigate('/backofice/agregarempresa');

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];

        setLogoFile(file || null);

        if (file) {
            setPreviewLogo(URL.createObjectURL(file));
        } else {
            setPreviewLogo('');
        }
    };

    const getEstadoColor = (valor) => {
        switch (valor) {
            case 'SUSPENDIDA':
                return 'orange';
            case 'INACTIVA':
                return 'grey';
            case 'ACTIVA':
            default:
                return 'green';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        cambiarEstadoAlerta(false);
        cambiarAlerta({});

        const emp = (nuevaEmpresa || '').trim().toUpperCase();

        if (!emp) {
            cambiarEstadoAlerta(true);
            cambiarAlerta({
                tipo: 'error',
                mensaje: 'Campo empresa no puede estar vacío',
            });
            return;
        }

        if (!rut.trim()) {
            cambiarEstadoAlerta(true);
            cambiarAlerta({
                tipo: 'error',
                mensaje: 'Campo RUT no puede estar vacío',
            });
            return;
        }

        if (!maxUsers || Number(maxUsers) < 1) {
            cambiarEstadoAlerta(true);
            cambiarAlerta({
                tipo: 'error',
                mensaje: 'Máximo de usuarios debe ser mayor a 0',
            });
            return;
        }

        setIsSaving(true);

        try {
            let nuevoLogoUrl = logoUrl;
            let nuevoLogoPath = logoPath;
            let cambioLogo = false;

            if (logoFile) {
                const logoData = await subirLogoEmpresa({
                    file: logoFile,
                    empId: id,
                });

                nuevoLogoUrl = logoData.logoUrl;
                nuevoLogoPath = logoData.logoPath;
                cambioLogo = true;
            }

            await ActualizarEmpresaDb({
                id,
                empresa: emp,
                rut: rut.trim(),
                plan: plan || 'BASICO',
                estado: estado || 'ACTIVA',
                maxUsers: Number(maxUsers || 5),
                logoUrl: nuevoLogoUrl || '',
                logoPath: nuevoLogoPath || '',
                contrato_mantencion: contratoMantencion,
                usa_logisticmed_st: contratoMantencion ? usaLogisticmedSt : false,
                userMod: user?.email || '',
                fechaMod: new Date(),
            });

            if (cambioLogo) {
                await updateDoc(doc(db, 'empresas', id), {
                    versionLogo: increment(1),
                    fechaMod: new Date(),
                    userMod: user?.email || '',
                });

                setVersionLogo((v) => Number(v || 1) + 1);
            }

            setLogoUrl(nuevoLogoUrl);
            setLogoPath(nuevoLogoPath);
            setLogoFile(null);
            setPreviewLogo('');

            cambiarEstadoAlerta(true);
            cambiarAlerta({
                tipo: 'exito',
                mensaje: 'Empresa actualizada exitosamente',
            });
        } catch (error) {
            console.error(error);
            cambiarEstadoAlerta(true);
            cambiarAlerta({
                tipo: 'error',
                mensaje: error.message || 'Error al actualizar empresa',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Container fluid style={{ padding: '22px 28px' }}>
            <Segment basic style={{ padding: 0, marginBottom: 18 }}>
                <div
                    style={{
                        background: 'linear-gradient(135deg, #0f6f78 0%, #16b9cf 100%)',
                        borderRadius: 16,
                        padding: '26px 30px',
                        color: '#fff',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                    }}
                >
                    <Grid stackable verticalAlign="middle">
                        <Grid.Column width={10}>
                            <Header as="h2" inverted style={{ margin: 0 }}>
                                <Icon name="building outline" />
                                <Header.Content>
                                    Actualizar Empresa
                                    <Header.Subheader style={{ color: 'rgba(255,255,255,0.85)' }}>
                                        Configura datos comerciales, logo, plan y estado del cliente
                                    </Header.Subheader>
                                </Header.Content>
                            </Header>
                        </Grid.Column>

                        <Grid.Column width={6} textAlign="right">
                            <Label size="large" basic style={{ color: '#fff', borderColor: '#fff' }}>
                                <Icon name="settings" />
                                Backoffice
                            </Label>
                        </Grid.Column>
                    </Grid>
                </div>
            </Segment>

            <Grid stackable>
                <Grid.Column width={5}>
                    <Segment
                        raised
                        style={{
                            borderRadius: 16,
                            border: '1px solid #e7edf2',
                            boxShadow: '0 8px 22px rgba(0,0,0,0.07)',
                        }}
                    >
                        <Header as="h3" color="teal">
                            <Icon name="image outline" />
                            <Header.Content>
                                Branding
                                <Header.Subheader>Logo visible en navbar, PDFs y correos</Header.Subheader>
                            </Header.Content>
                        </Header>

                        <Divider />

                        <div
                            style={{
                                border: '1px dashed #b9c7d3',
                                borderRadius: 14,
                                padding: 18,
                                background: '#f8fbfd',
                                textAlign: 'center',
                            }}
                        >
                            <p style={{ fontWeight: 700, marginBottom: 10 }}>Logo actual</p>

                            <Image
                                src={logoSrc}
                                centered
                                style={{
                                    maxHeight: 110,
                                    maxWidth: 210,
                                    objectFit: 'contain',
                                    background: '#fff',
                                    padding: 10,
                                    borderRadius: 10,
                                    border: '1px solid #e6e6e6',
                                    marginBottom: 14,
                                }}
                                onError={(e) => {
                                    if (e.currentTarget.src.includes('/logo-default.png')) return;
                                    e.currentTarget.src = '/logo-default.png';
                                }}
                            />

                            <Button
                                as="label"
                                icon
                                basic
                                color="teal"
                                fluid
                                disabled={isSaving}
                            >
                                <Icon name="upload" />
                                Cambiar logo
                                <input
                                    hidden
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/webp"
                                    onChange={handleLogoChange}
                                />
                            </Button>

                            {logoFile && (
                                <div style={{ fontSize: 12, marginTop: 8, color: '#607080' }}>
                                    {logoFile.name}
                                </div>
                            )}
                        </div>

                        <Segment
                            basic
                            style={{
                                background: '#f7fafc',
                                borderRadius: 12,
                                marginTop: 16,
                                padding: 14,
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                <span>Plan</span>
                                <b>{plan}</b>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 6 }}>
                                <span>Usuarios permitidos</span>
                                <b>{maxUsers}</b>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 6 }}>
                                <span>Estado</span>
                                <Label color={getEstadoColor(estado)} size="mini">
                                    {estado}
                                </Label>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 6 }}>
                                <span>Contrato mantención</span>
                                <Label color={contratoMantencion ? 'green' : 'grey'} size="mini">
                                    {contratoMantencion ? 'ACTIVO' : 'NO'}
                                </Label>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 6 }}>
                                <span>LogisticMedST</span>
                                <Label color={usaLogisticmedSt ? 'teal' : 'grey'} size="mini">
                                    {usaLogisticmedSt ? 'SÍ' : 'NO'}
                                </Label>
                            </div>
                        </Segment>
                    </Segment>
                </Grid.Column>

                <Grid.Column width={11}>
                    <Segment
                        raised
                        style={{
                            borderRadius: 16,
                            border: '1px solid #e7edf2',
                            boxShadow: '0 8px 22px rgba(0,0,0,0.07)',
                        }}
                    >
                        <Header as="h3" color="blue">
                            <Icon name="edit outline" />
                            <Header.Content>
                                Datos de la empresa
                                <Header.Subheader>Información principal del cliente</Header.Subheader>
                            </Header.Content>
                        </Header>

                        <Divider />

                        <Form onSubmit={handleSubmit}>
                            <Form.Group widths="equal">
                                <Form.Input
                                    fluid
                                    label="RUT"
                                    placeholder="Ej: 17579501-4"
                                    value={rut}
                                    onChange={(e) => setRut(e.target.value)}
                                    icon={{ name: 'id card outline', color: 'teal' }}
                                    iconPosition="left"
                                    disabled={isSaving}
                                />

                                <Form.Input
                                    fluid
                                    label="Nombre empresa"
                                    placeholder="Nombre empresa"
                                    value={nuevaEmpresa}
                                    onChange={(e) => setNuevaEmpresa(e.target.value)}
                                    icon={{ name: 'building outline', color: 'teal' }}
                                    iconPosition="left"
                                    disabled={isSaving}
                                />
                            </Form.Group>

                            <Form.Group widths="equal">
                                <Form.Select
                                    fluid
                                    label="Plan"
                                    value={plan}
                                    onChange={(e, { value }) => setPlan(value)}
                                    options={[
                                        { key: 'BASICO', text: 'Básico', value: 'BASICO' },
                                        { key: 'PRO', text: 'Pro', value: 'PRO' },
                                        { key: 'ENTERPRISE', text: 'Enterprise', value: 'ENTERPRISE' },
                                    ]}
                                    disabled={isSaving}
                                />

                                <Form.Input
                                    fluid
                                    label="Máximo usuarios"
                                    type="number"
                                    min={1}
                                    value={maxUsers}
                                    onChange={(e) => setMaxUsers(Number(e.target.value))}
                                    icon={{ name: 'users', color: 'teal' }}
                                    iconPosition="left"
                                    disabled={isSaving}
                                />

                                <Form.Select
                                    fluid
                                    label="Estado"
                                    value={estado}
                                    onChange={(e, { value }) => setEstado(value)}
                                    options={[
                                        { key: 'ACTIVA', text: 'Activa', value: 'ACTIVA' },
                                        { key: 'SUSPENDIDA', text: 'Suspendida', value: 'SUSPENDIDA' },
                                        { key: 'INACTIVA', text: 'Inactiva', value: 'INACTIVA' },
                                    ]}
                                    disabled={isSaving}
                                />
                            </Form.Group>

                            <Segment
                                basic
                                style={{
                                    background: '#f0fdfa',
                                    borderRadius: 12,
                                    marginTop: 14,
                                    padding: 14,
                                    border: '1px solid #ccfbf1',
                                }}
                            >
                                <Header as="h4" color="teal" style={{ marginBottom: 12 }}>
                                    <Icon name="wrench" />
                                    Contrato de mantención
                                </Header>

                                <Form.Checkbox
                                    toggle
                                    label="Cliente con contrato de mantención"
                                    checked={contratoMantencion}
                                    onChange={(e, { checked }) => {
                                        setContratoMantencion(checked);

                                        if (!checked) {
                                            setUsaLogisticmedSt(false);
                                        }
                                    }}
                                    disabled={isSaving}
                                />

                                <Form.Checkbox
                                    toggle
                                    label="Derivar equipos cubiertos a LogisticMedST"
                                    checked={usaLogisticmedSt}
                                    onChange={(e, { checked }) => setUsaLogisticmedSt(checked)}
                                    disabled={isSaving || !contratoMantencion}
                                    style={{ marginTop: 10 }}
                                />

                                <div style={{ marginTop: 10, color: '#64748b', fontSize: 12 }}>
                                    La cobertura de familias, tipos o equipos se administra posteriormente desde Backoffice.
                                </div>
                            </Segment>

                            <Divider />

                            <Card.Group stackable itemsPerRow={3}>
                                <Card>
                                    <Card.Content>
                                        <Icon name="building" color="teal" size="large" />
                                        <Card.Header style={{ marginTop: 10 }}>
                                            {nuevaEmpresa || '-'}
                                        </Card.Header>
                                        <Card.Meta>Empresa</Card.Meta>
                                    </Card.Content>
                                </Card>

                                <Card>
                                    <Card.Content>
                                        <Icon name="user shield" color="blue" size="large" />
                                        <Card.Header style={{ marginTop: 10 }}>
                                            {plan}
                                        </Card.Header>
                                        <Card.Meta>Plan contratado</Card.Meta>
                                    </Card.Content>
                                </Card>

                                <Card>
                                    <Card.Content>
                                        <Icon name="users" color="green" size="large" />
                                        <Card.Header style={{ marginTop: 10 }}>
                                            {maxUsers}
                                        </Card.Header>
                                        <Card.Meta>Usuarios máximos</Card.Meta>
                                    </Card.Content>
                                </Card>
                            </Card.Group>

                            <Divider />

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 10,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    type="button"
                                    basic
                                    color="blue"
                                    onClick={volver}
                                    disabled={isSaving}
                                >
                                    <Icon name="arrow left" />
                                    Volver
                                </Button>

                                <Button
                                    primary
                                    type="submit"
                                    loading={isSaving}
                                    disabled={isSaving}
                                    style={{
                                        borderRadius: 10,
                                        background: '#16B9CF',
                                    }}
                                >
                                    <Icon name="save outline" />
                                    Guardar Cambios
                                </Button>
                            </div>
                        </Form>
                    </Segment>
                </Grid.Column>
            </Grid>

            <Alerta
                tipo={alerta.tipo}
                mensaje={alerta.mensaje}
                estadoAlerta={estadoAlerta}
                cambiarEstadoAlerta={cambiarEstadoAlerta}
            />
        </Container>
    );
};

export default ActualizaEmpresa;